import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import StatusBadge from "@/components/app/StatusBadge";
import { Inbox, CheckCircle2, XCircle, ShieldQuestion, Plus } from "lucide-react";

const CONF_TONE = {
  exact: "green", high: "blue", possible: "amber", none: "red", manual: "slate",
};

export default function DiscoveryInbox() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/discovery/inbox", { params: { status: status !== "all" ? status : undefined, limit: 100 } })
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));
  };
  useEffect(load, [status]);

  const act = async (id, action, extra = {}) => {
    try {
      await api.post(`/discovery/${id}/reconcile`, { action, ...extra });
      toast.success(`Marked ${action}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4 max-w-[1600px]">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Asset Intelligence</div>
        <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">Discovery Inbox</h1>
        <p className="text-sm text-slate-500 mt-0.5">Raw records from connected systems awaiting reconciliation into the Golden Record.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-3">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-9 w-56"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["all", "auto_matched", "review", "unknown", "matched", "created", "ignored", "quarantined"].map((s) =>
              <SelectItem key={s} value={s}><span className="capitalize">{s.replace(/_/g, " ")}</span></SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-[10px] uppercase text-slate-500">Source</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500">Device</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500">Serial</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500">Match</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500">Status</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500">Last seen</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-sm text-slate-500">Loading…</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-sm text-slate-500 flex-col items-center">
                <Inbox className="h-6 w-6 text-slate-300 mx-auto mb-2" /> No records to reconcile.
              </TableCell></TableRow>
            ) : items.map((r) => (
              <TableRow key={r.id} className="hover:bg-slate-50">
                <TableCell className="text-xs uppercase font-semibold text-slate-700">{r.system}</TableCell>
                <TableCell className="text-sm">
                  <div className="text-slate-800">{r.manufacturer} {r.model}</div>
                  <div className="text-xs text-slate-500 font-mono">{r.device_name}</div>
                </TableCell>
                <TableCell className="font-mono text-xs">{r.serial || "—"}</TableCell>
                <TableCell><StatusBadge value={r.match_confidence || "none"} tone={CONF_TONE[r.match_confidence] || "slate"} /></TableCell>
                <TableCell><StatusBadge value={r.status} /></TableCell>
                <TableCell className="text-xs text-slate-500">{r.last_seen ? new Date(r.last_seen).toLocaleString() : "—"}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    {r.matched_asset_id && r.status !== "matched" && (
                      <Button size="sm" variant="outline" onClick={() => act(r.id, "match", { asset_id: r.matched_asset_id })}>
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Match
                      </Button>
                    )}
                    {!r.matched_asset_id && r.status === "unknown" && (
                      <Button size="sm" variant="outline" onClick={() => act(r.id, "create")}>
                        <Plus className="h-3.5 w-3.5 mr-1" /> Create
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => act(r.id, "quarantine")}>
                      <ShieldQuestion className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => act(r.id, "ignore")}>
                      <XCircle className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
